import { Component, Input, OnInit, Output, ViewChild, EventEmitter, OnDestroy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import * as MaintenanceReducer from "@app/maintenance/store/maintenance.reducer"
import { Store, select } from '@ngrx/store';
import { Subscription, Observable } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';
import { Log } from "ng2-logger";

import { TaskInstructionsTreeModalComponent } from '../../task-instructions-tree-modal/task-instructions-tree-modal.component';
import { TaskPhotoUploadModalComponent } from '../../task-photo-upload-modal/task-photo-upload-modal.component';
import { AssignationDropdownComponent } from '../../assignation-dropdown/assignation-dropdown.component';
import { taskUnits } from "@app/shared/models/consts/taskUnits.const";
import { TaskInstructionType } from '@app/shared/models/enums/task-instruction-type.enum';
import { ToastsManager } from "@app/shared/util/toastr.service";
import { ActivityTask } from '@models/activity-task.model';
import { ActivatedRoute, Router } from '@angular/router';
import { NavigationService } from '@app/core/services/navigation.service';
import { TasksService } from '@app/maintenance/service/tasks.service';

import * as FileSaver from 'file-saver';
import {
    LoadTaskSpareInstructionFilesAction,
    LoadTaskWorkInstructionFilesAction
} from "@app/maintenance/store/maintenance.actions";

@Component({
    selector: 'app-task-form',
    templateUrl: './task-form.component.html',
    styleUrls: ['./task-form.component.scss']
})
export class TaskFormComponent implements OnInit, OnDestroy {
    _logger = Log.create(TaskFormComponent.name);

    @ViewChild(AssignationDropdownComponent, {static: false}) dropdown: AssignationDropdownComponent;
    @ViewChild(TaskPhotoUploadModalComponent, {static: false}) photoModal: TaskPhotoUploadModalComponent;
    @ViewChild("workModal", {static: false}) instructionModal: TaskInstructionsTreeModalComponent;
    @ViewChild("spareModal", {static: false}) spareModal: TaskInstructionsTreeModalComponent;

    @Input() roles = [];
    @Input() showErrors = false;
    @Input() isEdit = false;
    @Input() readonly = false;
    @Input() selectedTask;

    @Output() create = new EventEmitter<any>();
    @Output() update = new EventEmitter<any>();
    @Output() close = new EventEmitter<any>();

    assigned: any;
    units = [];
    images: any = [];
    activity: any;
    task: ActivityTask;

    formGroup: UntypedFormGroup;
    isLoading = false;

    workInstructionFiles: any;
    spareInstructionFiles: any;

    activity$: Observable<any>;
    updatedActivity$: Observable<any>;
    isLoadingFiles$: Observable<boolean>;
    workInstructionFiles$: Observable<any>;
    spareInstructionFiles$: Observable<any>;

    activitySubscription: Subscription;
    updatedActivitySubscription: Subscription;
    workInstructionFilesSubscription: Subscription;
    spareInstructionFilesSubscription: Subscription;

    constructor(private store: Store<any>,
                private translate: TranslateService,
                private toastr: ToastsManager,
                private router: Router,
                private route: ActivatedRoute,
                private navigationService: NavigationService,
                private tasksService: TasksService) {
        this.activity$ = this.store.pipe(select(MaintenanceReducer.getActivity));
        this.updatedActivity$ = this.store.pipe(select(MaintenanceReducer.getUpdatedActivity));
        this.isLoadingFiles$ = this.store.pipe(select(MaintenanceReducer.isLoadingFiles));
        this.workInstructionFiles$ = this.store.pipe(select(MaintenanceReducer.workInstructionFiles));
        this.spareInstructionFiles$ = this.store.pipe(select(MaintenanceReducer.spareInstructionFiles));

        this.activitySubscription = this.activity$.subscribe(activity => {
            if (activity) {
                this.activity = activity;
            }
        });

        this.updatedActivitySubscription = this.updatedActivity$.subscribe(activity => {
            if (activity) {
                this.activity = activity;
            }
        });

        this.workInstructionFilesSubscription = this.workInstructionFiles$.subscribe(data => {
            if (data) {
                this.workInstructionFiles = data;
            }
        });

        this.spareInstructionFilesSubscription = this.spareInstructionFiles$.subscribe(data => {
            this.spareInstructionFiles = data;
        });


    }

    ngOnInit() {
        this._logger.info("ngOnInit:: Preparing task form");

        this.initEmpty();

        if (this.selectedTask) {
            this.init(this.selectedTask)
        }

        if (!this.isEdit) {
            this.loadFiles();
        }
    }

    initEmpty() {
        this.task = null;
        this.units = taskUnits;
        this.images = [];

        this.formGroup = new UntypedFormGroup({
            name: new UntypedFormControl('', Validators.required),
            description: new UntypedFormControl('', Validators.required),
            identifier: new UntypedFormControl('', Validators.required),
            requiresObservation: new UntypedFormControl(false),
            duration: new UntypedFormControl('', [Validators.required, Validators.pattern("^[1-9][0-9]*$")]),
            unit: new UntypedFormControl('', Validators.required),
            workInstructions: new UntypedFormControl(''),
            photo: new UntypedFormControl([]),
            hasSpare: new UntypedFormControl(false),
            mandatorySpare: new UntypedFormControl(false),
            spareCode: new UntypedFormControl('', Validators.required),
            spareQuantity: new UntypedFormControl('', [Validators.required, Validators.pattern("^[1-9][0-9]*$")]),
            spareInstructions: new UntypedFormControl('')
        });
    }

    init(task: any) {
        this.task = task;
        this.units = this.readonly ? [taskUnits.find(u => u.label === task.unit)] : taskUnits;
        this.images = task.photos;
        this.loadFiles(task);

        this.formGroup = new UntypedFormGroup({
            name: new UntypedFormControl(task.name, Validators.required),
            description: new UntypedFormControl(task.description, Validators.required),
            identifier: new UntypedFormControl(task.identifier, Validators.required),
            requiresObservation: new UntypedFormControl(task.requiresObservation),
            duration: new UntypedFormControl(task.duration, [Validators.required, Validators.pattern("^[1-9][0-9]*$")]),
            unit: new UntypedFormControl(this.units.find(u => u.label === task.unit).id, Validators.required),
            workInstructions: new UntypedFormControl(this.getTaskFilePathByType(task, TaskInstructionType.WORK)),
            photo: new UntypedFormControl([]),
            hasSpare: new UntypedFormControl(task.hasSpare),
            mandatorySpare: new UntypedFormControl(task.mandatorySpare),
            spareCode: new UntypedFormControl(task.spareCode, Validators.required),
            spareQuantity: new UntypedFormControl(task.spareQuantity, [Validators.required, Validators.pattern("^[1-9][0-9]*$")]),
            spareInstructions: new UntypedFormControl(this.getTaskFilePathByType(task, TaskInstructionType.SPARE))
        });
    }

    loadFiles(task = null) {
        if (this.readonly) {
            return;
        }
        const taskId = task ? this.task.id : null;
        this.store.dispatch(new LoadTaskWorkInstructionFilesAction(this.activity.id, taskId));
        this.store.dispatch(new LoadTaskSpareInstructionFilesAction(this.activity.id, taskId));
    }

    openPhotoModal() {
        this.photoModal.open(this.images);
    }

    clickWorkInstructions() {
        if (!this.readonly) {
            this.instructionModal.open(this.workInstructionFiles, this.activity.rootPath);
            return;
        }

        const workInstructionFile = this.task && this.task.files ?
            this.task.files.find(item => item.type === TaskInstructionType.WORK) : null;
        if (workInstructionFile) {
            this.downloadTaskDocument(this.task.id, workInstructionFile.id);
        }
    }

    clickSpareInstructions() {
        if (!this.readonly) {
            this.spareModal.open(this.spareInstructionFiles, this.activity.rootPath);
            return;
        }

        const workInstructionFile = this.task && this.task.files ?
            this.task.files.find(item => item.type === TaskInstructionType.SPARE) : null;
        if (workInstructionFile) {
            this.downloadTaskDocument(this.task.id, workInstructionFile.id);
        }
    }


    downloadTaskDocument(taskId: number, documentId: number) {
        this.tasksService.downloadTaskDocument(taskId, documentId).subscribe(
            (data: any) => {
                if (data.body != null) {
                    const filename = data.headers.get('x-filename');
                    FileSaver.saveAs(data.body, filename);
                }
            }
        );
    }

    onConfirmPhotoUpload(event) {
        this.images = event.carouselImages;
        this.formGroup.controls.photo.setValue(event);
    }

    updateInstructionDocument(event) {
        if (event.type === TaskInstructionType.WORK) {
            this.formGroup.controls.workInstructions.setValue(event.fullPath);
            this.selectNode(this.workInstructionFiles, event.fullPath);
        } else {
            this.formGroup.controls.spareInstructions.setValue(event.fullPath);
            this.selectNode(this.spareInstructionFiles, event.fullPath);
        }
    }

    selectNode(nodeList, fullPath) {
        nodeList.forEach((node) => {
            if (node.fullPath === fullPath) {
                node.isSelected = true;
            } else {
                node.isSelected = false;
            }
            this.selectNode(node.items || [], fullPath);
        })
    }

    onCreate() {
        this.setFormValidators();
        if (!this.formGroup.valid) {
            this.showErrors = true;
            return;
        }

        if (!this.dropdown.selectedItem) {
            this.toastr.error(
                this.translate.instant('admin.toolMaintenancePlans.activities.modal.assignRole')
            );
            return;
        }

        this.showErrors = false;
        if (!this.isEdit) {
            this.create.emit(this.getFormValue());
        } else {
            this.update.emit(this.getFormValue());
        }
        this.onClose();
    }

    onClose() {
        this.close.emit();
    }

    ngOnDestroy() {
        if (this.activitySubscription) {
            this.activitySubscription.unsubscribe();
        }

        if (this.updatedActivitySubscription) {
            this.updatedActivitySubscription.unsubscribe();
        }

        if (this.spareInstructionFilesSubscription) {
            this.spareInstructionFilesSubscription.unsubscribe();
        }

        if (this.workInstructionFilesSubscription) {
            this.workInstructionFilesSubscription.unsubscribe();
        }
    }

    setFormValidators() {
        this._logger.info("setFormValidators:: Setting task details form validators for task details form", this.formGroup);
        switch (this.formGroup.controls.hasSpare.value) {
            case false:
                this.formGroup.controls.spareCode.setValidators([]);
                this.formGroup.controls.spareQuantity.setValidators([]);
                this.formGroup.controls.spareCode.updateValueAndValidity();
                this.formGroup.controls.spareQuantity.updateValueAndValidity();
                break;
            case true:
                this.formGroup.controls.spareCode.setValidators([Validators.required]);
                this.formGroup.controls.spareQuantity.setValidators([Validators.required, Validators.pattern("^[1-9][0-9]*$")]);
                this.formGroup.controls.spareCode.updateValueAndValidity();
                this.formGroup.controls.spareQuantity.updateValueAndValidity();
                break;
        }
    }

    getFormValue() {
        return {
            ...this.formGroup.value,
            assigned: this.dropdown.selectedItem
        };
    }

    goToPlan(id: number) {
        this.router.navigate([`../../../plans/${id}/details/`], {relativeTo: this.route});
        this.navigationService.addToHistory(this.router.url);
    }

    getPathWithoutRoot(path: string) {
        return this.activity ? path.replace(this.activity.rootPath + '\\', '') : path;
    }

    getFileName(path: string) {
        const splitPath = this.getPathWithoutRoot(path).split("\\");
        return splitPath[splitPath.length - 1];
    }

    getPlaceholder() {
        if (this.readonly || this.isEdit) {
            return this.images.length + this.translate.instant('admin.toolMaintenancePlans.tasks.createModal.photoModal.label')
        }
        return this.images.length > 0
            ? this.images.length + this.translate.instant('admin.toolMaintenancePlans.tasks.createModal.photoModal.label')
            : this.translate.instant('admin.toolMaintenancePlans.tasks.createModal.photoModal.button');
    }

    getTaskFilePathByType(task: ActivityTask, type: TaskInstructionType) {
        const file = this.task && this.task.files ?
            this.task.files.find(item => item.type === type) : null;

        return file ? file.fullPath : '';
    }

    getSelectedItem() {
        return this.task ? this.roles.find(item => item.id === this.task.assignedRoleId) : null;
    }
}
